import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft, Edit, Users, Plus } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function BrokerManagement() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [salesReps, setSalesReps] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedSalesRep, setSelectedSalesRep] = useState(null);
  const [editingProjects, setEditingProjects] = useState([]);
  const [editingName, setEditingName] = useState('');
  const [editingEmail, setEditingEmail] = useState(''); // Change 1: State for editing email
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Add team member states
  const [addMemberDialog, setAddMemberDialog] = useState(false);
  const [newMember, setNewMember] = useState({
    full_name: '',
    email: '',
    contact_no: '',
    user_role: 'Sales Rep',
    projects_assigned: []
  });

  useEffect(() => {
    if (user?.user_role !== 'Super Administrator' && user?.user_role !== 'Manager') {
      navigate('/');
      return;
    }
    loadData();
  }, [user]);

  const loadData = async () => {
    const token = localStorage.getItem('token');
    try {
      const [salesRepsRes, projectsRes] = await Promise.all([
        axios.get(`${API}/brokers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/projects`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setSalesReps(salesRepsRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSalesRep = (salesRep) => {
    setSelectedSalesRep(salesRep);
    setEditingProjects(salesRep.projects_assigned || []);
    setEditingName(salesRep.full_name || '');
    setEditingEmail(salesRep.email || ''); // Change 1: Track email editing
    setDialogOpen(true);
  };

  const handleProjectToggle = (projectName) => {
    setEditingProjects(prev => 
      prev.includes(projectName)
        ? prev.filter(p => p !== projectName)
        : [...prev, projectName]
    );
  };

  const handleSaveProjects = async () => {
    const token = localStorage.getItem('token');
    try {
      // Include full_name and email in update if changed
      const updates = { projects_assigned: editingProjects };
      if (editingName && editingName !== selectedSalesRep.full_name) {
        updates.full_name = editingName;
      }
      // Change 1: Include email if changed
      if (editingEmail && editingEmail !== selectedSalesRep.email) {
        updates.email = editingEmail;
      }
      
      await axios.put(
        `${API}/brokers/${selectedSalesRep.id}`,
        updates,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Sales rep updated successfully');
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to update sales rep');
    }
  };

  const handleToggleActive = async (salesRepId, currentStatus) => {
    const token = localStorage.getItem('token');
    try {
      await axios.put(
        `${API}/brokers/${salesRepId}`,
        { active_status: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('SalesRep status updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Add team member function
  const handleAddMember = async () => {
    if (!newMember.full_name || !newMember.email) {
      toast.error('Please fill in required fields');
      return;
    }

    setLoading(true);
    const token = localStorage.getItem('token');

    try {
      await axios.post(`${API}/auth/register`, {
        full_name: newMember.full_name,
        email: newMember.email,
        password: 'password123', // Default password for new team members
        contact_no: newMember.contact_no,
        user_role: newMember.user_role,
        projects_assigned: newMember.projects_assigned
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Team member added successfully');
      setAddMemberDialog(false);
      setNewMember({
        full_name: '',
        email: '',
        contact_no: '',
        user_role: 'Sales Rep',
        projects_assigned: []
      });
      loadData();
    } catch (error) {
      console.error('Add member error:', error);
      
      // Handle Pydantic validation errors
      if (error.response?.status === 422 && error.response?.data?.detail) {
        const details = error.response.data.detail;
        if (Array.isArray(details)) {
          // Multiple validation errors
          const errorMessages = details.map(err => `${err.loc?.join('.')}: ${err.msg}`).join(', ');
          toast.error(`Validation error: ${errorMessages}`);
        } else if (typeof details === 'string') {
          toast.error(details);
        } else {
          toast.error('Validation error occurred');
        }
      } else {
        toast.error(error.response?.data?.detail || 'Failed to add team member');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleProjectToggleForNewMember = (projectId) => {
    setNewMember(prev => ({
      ...prev,
      projects_assigned: prev.projects_assigned.includes(projectId)
        ? prev.projects_assigned.filter(p => p !== projectId)
        : [...prev.projects_assigned, projectId]
    }));
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="salesrep-management-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/')} data-testid="back-button">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope' }}>Sales Rep Management</h1>
                <p className="text-sm text-slate-600">Manage team members and project assignments</p>
              </div>
            </div>
            <Button 
              onClick={() => setAddMemberDialog(true)}
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Team Member
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 space-y-4">
        {/* salesReps List */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {salesReps.map(salesRep => (
            <Card key={salesRep.id} data-testid={`salesrep-card-${salesRep.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-base font-semibold">{salesRep.full_name}</CardTitle>
                    <p className="text-xs text-slate-600 mt-1">{salesRep.email}</p>
                    <Badge className="mt-2" variant={salesRep.user_role === 'Super Administrator' ? 'default' : 'secondary'}>
                      {salesRep.user_role}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={salesRep.active_status}
                      onCheckedChange={() => handleToggleActive(salesRep.id, salesRep.active_status)}
                      data-testid={`active-toggle-${salesRep.id}`}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <div className="text-xs text-slate-600 font-medium mb-1">Projects Assigned:</div>
                  <div className="text-sm font-bold text-cyan-700">
                    {salesRep.projects_assigned?.length || 0} / {projects.length}
                  </div>
                </div>
                {salesRep.contact_no && (
                  <div className="text-xs text-slate-600">
                    Contact: {salesRep.contact_no}
                  </div>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full"
                  onClick={() => handleEditSalesRep(salesRep)}
                  data-testid={`edit-projects-${salesRep.id}`}
                >
                  <Edit className="mr-2 h-3 w-3" />
                  Assign Projects
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Edit Projects Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Sales Rep: {selectedSalesRep?.full_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Name editing field */}
            <div className="space-y-2">
              <Label htmlFor="edit-name">Full Name</Label>
              <Input
                id="edit-name"
                value={editingName}
                onChange={(e) => setEditingName(e.target.value)}
                placeholder="Enter full name"
                data-testid="edit-name-input"
              />
            </div>
            
            {/* Change 1: Email editing field */}
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editingEmail}
                onChange={(e) => setEditingEmail(e.target.value)}
                placeholder="Enter email address"
                data-testid="edit-email-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Assign Projects</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 border rounded-md p-3">
                {projects.map(project => (
                  <div key={project.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`edit-${project.id}`}
                      checked={editingProjects.includes(project.project_name)}
                      onCheckedChange={() => handleProjectToggle(project.project_name)}
                      data-testid={`project-checkbox-${project.id}`}
                    />
                    <label
                      htmlFor={`edit-${project.id}`}
                      className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      {project.project_name}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            <div className="text-sm text-slate-600">
              {editingProjects.length} project(s) selected
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveProjects} className="bg-cyan-600 hover:bg-cyan-700" data-testid="save-projects-button">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Team Member Dialog */}
      <Dialog open={addMemberDialog} onOpenChange={setAddMemberDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Team Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="full_name">Full Name *</Label>
                <Input
                  id="full_name"
                  value={newMember.full_name}
                  onChange={(e) => setNewMember({...newMember, full_name: e.target.value})}
                  placeholder="Enter full name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={newMember.email}
                  onChange={(e) => setNewMember({...newMember, email: e.target.value})}
                  placeholder="Enter email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_no">Contact Number</Label>
                <Input
                  id="contact_no"
                  value={newMember.contact_no}
                  onChange={(e) => {
                    // Allow + prefix and digits, with optional formatting (spaces, dashes, parentheses)
                    let value = e.target.value;
                    // Remove all non-digit, non-formatting characters except + at start
                    value = value.replace(/[^\d+\s\-()]/g, '');
                    // Ensure + only appears at the start
                    if (value.includes('+') && !value.startsWith('+')) {
                      value = '+' + value.replace(/\+/g, '');
                    }
                    // Limit to 20 characters to allow for formatting
                    if (value.length <= 20) {
                      setNewMember({...newMember, contact_no: value});
                    }
                  }}
                  placeholder="Enter mobile number (e.g., +1234567890 or 1234567890)"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="user_role">Role</Label>
                <Select
                  value={newMember.user_role}
                  onValueChange={(value) => setNewMember({...newMember, user_role: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Sales Rep">Sales Rep</SelectItem>
                    <SelectItem value="Manager">Manager</SelectItem>
                    <SelectItem value="Super Administrator">Super Administrator</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Assign Projects</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-4 border rounded-lg">
                {projects.map(project => (
                  <div key={project.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`new-${project.id}`}
                      checked={newMember.projects_assigned.includes(project.project_name)}
                      onCheckedChange={() => handleProjectToggleForNewMember(project.project_name)}
                    />
                    <label
                      htmlFor={`new-${project.id}`}
                      className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      {project.project_name}
                    </label>
                  </div>
                ))}
              </div>
              <div className="text-sm text-slate-600">
                {newMember.projects_assigned.length} project(s) selected
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddMemberDialog(false)}>Cancel</Button>
            <Button onClick={handleAddMember} className="bg-cyan-600 hover:bg-cyan-700" disabled={loading}>
              {loading ? 'Adding...' : 'Add Member'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default BrokerManagement;
